import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom hook to calculate center position for modals
 * Handles different units (px, vw, vh, %) and provides performance optimizations
 */
export const useModalPosition = () => {
  // Track viewport dimensions with state for responsiveness
  const [viewportDimensions, setViewportDimensions] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight,
  }));

  // Debounced resize handler
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Update viewport dimensions on resize with debouncing
  useEffect(() => {
    const handleResize = () => {
      // Clear existing timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce resize events
      resizeTimeoutRef.current = setTimeout(() => {
        setViewportDimensions({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }, 100); // 100ms debounce
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, []);

  // Memoized dimension parser with unit conversion
  const parseDimension = useCallback(
    (dimension: number | string | undefined, defaultValue: number): number => {
      if (typeof dimension === "number") return dimension;
      if (typeof dimension === "string") {
        const value = parseFloat(dimension);
        if (isNaN(value)) return defaultValue;

        // Handle viewport units (vw, vh)
        if (dimension.includes("vw")) {
          return (value / 100) * viewportDimensions.width;
        }
        if (dimension.includes("vh")) {
          return (value / 100) * viewportDimensions.height;
        }
        // Handle percentage (assume relative to width for consistency)
        if (dimension.includes("%")) {
          return (value / 100) * viewportDimensions.width;
        }
        // Handle pixels or other units
        return value;
      }
      return defaultValue;
    },
    [viewportDimensions]
  );

  // Memoized center position calculator
  const calculateCenterPosition = useCallback(
    (width?: number | string, height?: number | string) => {
      const modalWidth = parseDimension(width, 600);
      const modalHeight = parseDimension(height, 500);

      // Calculate center coordinates with bounds checking
      const centerX = Math.max(
        0,
        Math.min(
          (viewportDimensions.width - modalWidth) / 2,
          viewportDimensions.width - modalWidth
        )
      );
      const centerY = Math.max(
        0,
        Math.min(
          (viewportDimensions.height - modalHeight) / 2,
          viewportDimensions.height - modalHeight
        )
      );

      return { x: centerX, y: centerY };
    },
    [parseDimension, viewportDimensions]
  );

  return {
    calculateCenterPosition,
    parseDimension,
    viewportDimensions,
  };
};
